import React, {useEffect, useState} from 'react'
import {useUserProfileContext} from '../../../context/UserProfileContext'
import {
  getMemberCategories,
  getMemberAssignments,
  assignMemberCategory,
  confirmAssignment,
  removeAssignment,
  MemberCategory,
  MemberCategoryAssignment,
} from '../../../services/memberCategoriesService'
import {
  confirmedAssignments,
  proposedAssignments,
  unassignedCategories,
} from '../utils/memberCategories'

/**
 * PR-I-79 — staff category management for a member (Members admin, Categories tab).
 *
 * Estructura fija:
 *   1. "Assigned" (siempre visible, al inicio) — chips con ✕ para desasignar.
 *   2. "Assign a category" — chips del catálogo activo (backend ya filtra isActive=true y ordena por
 *      order), excluyendo tanto los assigned como los proposed (para no duplicar).
 *   3. "Proposed by member" (al final) — chips clickeables de sugerencias member_proposed;
 *      click confirma la sugerencia (staff_confirmed) y salta a Assigned.
 *
 * Feedback optimista: cada acción muta local y se revierte si el backend falla. En error de red
 * total (mount inicial) el componente degrada al aviso "unavailable".
 */
const MemberCategoriesAdmin: React.FC = () => {
  const {searchParams} = useUserProfileContext()
  const memberId = searchParams?.userSelected

  const [catalog, setCatalog] = useState<MemberCategory[]>([])
  const [assignments, setAssignments] = useState<MemberCategoryAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [unavailable, setUnavailable] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!memberId) {
        if (!cancelled) {
          setLoading(false)
          setUnavailable(true)
        }
        return
      }
      try {
        const [cats, mine] = await Promise.all([
          getMemberCategories(),
          getMemberAssignments(memberId),
        ])
        if (cancelled) return
        setCatalog(cats)
        setAssignments(mine)
        setUnavailable(false)
      } catch {
        if (!cancelled) setUnavailable(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [memberId])

  const handleAssign = async (category: MemberCategory) => {
    if (!memberId || busyId !== null) return
    setBusyId(category.id)
    const optimistic: MemberCategoryAssignment = {
      id: -category.id, // negative placeholder; replaced by backend response
      member: Number(memberId),
      category: category.id,
      categoryName: category.name,
      isPrimary: false,
      status: 'staff_confirmed',
    }
    const previous = assignments
    setAssignments([...previous, optimistic])
    try {
      const saved = await assignMemberCategory(memberId, category.id)
      setAssignments((current) =>
        current.map((a) => (a.id === optimistic.id ? saved : a))
      )
    } catch {
      setAssignments(previous)
    } finally {
      setBusyId(null)
    }
  }

  const handleConfirmProposed = async (assignment: MemberCategoryAssignment) => {
    if (busyId !== null) return
    setBusyId(assignment.id)
    const previous = assignments
    setAssignments(
      previous.map((a) =>
        a.id === assignment.id ? {...a, status: 'staff_confirmed'} : a
      )
    )
    try {
      const saved = await confirmAssignment(assignment.id)
      setAssignments((current) =>
        current.map((a) => (a.id === assignment.id ? saved : a))
      )
    } catch {
      setAssignments(previous)
    } finally {
      setBusyId(null)
    }
  }

  const handleUnassign = async (assignment: MemberCategoryAssignment) => {
    if (busyId !== null) return
    setBusyId(assignment.id)
    const previous = assignments
    setAssignments(previous.filter((a) => a.id !== assignment.id))
    try {
      await removeAssignment(assignment.id)
    } catch {
      setAssignments(previous)
    } finally {
      setBusyId(null)
    }
  }

  if (loading) return null
  if (unavailable) {
    return (
      <div className='pf-section member-categories-admin'>
        <div className='member-categories-admin__heading'>Assigned</div>
        <div className='pf-section__desc italic'>
          Category management isn’t available right now.
        </div>
      </div>
    )
  }

  const assigned = confirmedAssignments(assignments)
  const proposed = proposedAssignments(assignments)
  const assignable = unassignedCategories(catalog, assignments)

  return (
    <div className='pf-section member-categories-admin'>
      <div className='member-categories-admin__group'>
        <div className='member-categories-admin__heading'>Assigned</div>
        {assigned.length === 0 ? (
          <div className='pf-section__desc italic'>None yet.</div>
        ) : (
          <div className='pf-interests'>
            {assigned.map((a) => (
              <div
                key={a.id}
                className={`pf-chip pf-chip--assigned ${busyId === a.id ? 'is-busy' : ''}`}
              >
                <span className='pf-chip__label'>{a.categoryName}</span>
                <button
                  type='button'
                  className='pf-chip__remove'
                  aria-label={`Unassign ${a.categoryName}`}
                  disabled={busyId !== null}
                  onClick={() => handleUnassign(a)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className='member-categories-admin__group'>
        <div className='member-categories-admin__heading'>Proposed by member</div>
        {proposed.length === 0 ? (
          <div className='pf-section__desc italic'>None yet.</div>
        ) : (
          <div className='pf-interests'>
            {proposed.map((a) => (
              <div
                key={a.id}
                className={`pf-chip ${busyId === a.id ? 'is-busy' : ''}`}
                role='button'
                tabIndex={0}
                onClick={() => handleConfirmProposed(a)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleConfirmProposed(a)
                  }
                }}
                title='Confirm this suggestion'
              >
                {a.categoryName}
              </div>
            ))}
          </div>
        )}
      </div>

      {assignable.length > 0 && (
        <div className='member-categories-admin__group'>
          <div className='member-categories-admin__heading'>Assign a category</div>
          <div className='pf-interests'>
            {assignable.map((c) => (
              <div
                key={c.id}
                className={`pf-chip ${busyId === c.id ? 'is-busy' : ''}`}
                role='button'
                tabIndex={0}
                onClick={() => handleAssign(c)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleAssign(c)
                  }
                }}
              >
                {c.name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default MemberCategoriesAdmin
